// Mirrors the server's Manage.<Area>.View / Manage.<Area>.Edit policies
// (ManagementAccess.cs, and UserAdministration.cs for Users), so a page hides
// what the server would refuse.

const ADMINISTRATOR_ROLE = "Portal.Administrator";

interface AreaRoles {
  /** Any of these opens the area's screen. */
  view: string[];
  /** Any of these may change data on it. */
  edit: string[];
}

// Areas with View and Edit roles: View looks, Edit changes.
const viewEdit = (area: string): AreaRoles => ({
  view: [`${area}.View`, `${area}.Edit`],
  edit: [`${area}.Edit`],
});

// Areas with a single Manage role, which both opens and changes the screen.
const manage = (area: string): AreaRoles => ({
  view: [`${area}.Manage`],
  edit: [`${area}.Manage`],
});

/** The roles each Management area has in the roles table. */
const AREA_ROLES: Record<string, AreaRoles> = {
  Accounts: viewEdit("Accounts"),
  Tools: viewEdit("Tools"),
  Users: viewEdit("Users"),
  DatabaseExplorer: manage("DatabaseExplorer"),
  Forms: manage("Forms"),
  Groups: manage("Groups"),
  InvoiceDescriptions: manage("InvoiceDescriptions"),
  Links: manage("Links"),
  Notifications: manage("Notifications"),
  TextMessages: manage("TextMessages"),
  Timesheets: manage("Timesheets"),
  Vehicles: manage("Vehicles"),
  WorkTypes: manage("WorkTypes"),
};

const asList = (allowedRoutes: unknown): string[] =>
  Array.isArray(allowedRoutes) ? allowedRoutes : [];

export const isAdministrator = (allowedRoutes: unknown): boolean =>
  asList(allowedRoutes).includes(ADMINISTRATOR_ROLE);

const holdsAny = (allowedRoutes: unknown, roles: string[]): boolean => {
  const allowed = asList(allowedRoutes);
  return allowed.includes(ADMINISTRATOR_ROLE) || roles.some((role) => allowed.includes(role));
};

/** May open the area's screen. An unknown area is for administrators only. */
export const canViewArea = (allowedRoutes: unknown, area: string): boolean =>
  holdsAny(allowedRoutes, AREA_ROLES[area]?.view ?? []);

/** May change data on the area's screen. An unknown area is for administrators only. */
export const canEditArea = (allowedRoutes: unknown, area: string): boolean =>
  holdsAny(allowedRoutes, AREA_ROLES[area]?.edit ?? []);
